import React, { useEffect } from 'react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import Tooltip from '@material-ui/core/Tooltip';

import { ImageViewerData } from 'components/image-viewer/types';
import { isImageFromContentType } from 'utils/is-image';
import { DialogContext } from 'components/dialog/dialog-context';
import { ImageViewerDialog } from 'components/dialog/aisuk-dialogs/aisuk-image-viewer-dialog/image-viewer-dialog';
import { ImageViewerProps } from 'components/image-viewer/image-viewer';
import { useImageViewerDialogStyles } from 'components/dialog/aisuk-dialogs/aisuk-image-viewer-dialog/image-viewer-dialog-styles';
import { ArchivalUnitCopy } from 'types';

import { StructMapImageInfo } from './struct-map-image-info';
import { OnLeafClickFn, StructMapData } from './struct-map-types';
import { getFileUrl } from './utils';

type CustomProps = {
  parentEntity?: ArchivalUnitCopy;
};

export const useStructMapImages = (
  structMap?: StructMapData,
  parentEntity?: ArchivalUnitCopy
) => {
  const dialogStyles = useImageViewerDialogStyles();
  const [data, setData] = useState<ImageViewerData<StructMapData>[]>([]);
  const parentId = useRef<string>();
  const [defaultIndex, setDefaultIndex] = useState(0);
  const { open } = useContext(DialogContext);

  const loadNodeImages = useCallback((node?: StructMapData) => {
    return (
      node?.subDivisions?.reduce<ImageViewerData<StructMapData>[]>(
        (acc, subNode) => {
          if (
            !subNode.fileRef ||
            !isImageFromContentType(subNode.fileRef?.contentType)
          ) {
            return acc;
          }

          const element: ImageViewerData<StructMapData> = {
            url: getFileUrl(subNode.fileRef.id),
            label: subNode.fileRef.name,
            metadata: subNode,
          };

          return [...acc, element];
        },
        []
      ) ?? []
    );
  }, []);

  useEffect(() => {
    const imageViewerData = loadNodeImages(structMap);

    setData(imageViewerData);
    setDefaultIndex(0);
  }, [loadNodeImages, structMap]);

  const openInFullscreenFactory = useCallback(
    (index: number) => () => {
      const initialValues: ImageViewerProps<StructMapData, CustomProps> = {
        allData: data,
        defaultIndex: index,
        InfoComponent: StructMapImageInfo,
        customProps: {
          parentEntity,
        },
      };

      open({
        Content: ImageViewerDialog,
        fullScreen: true,
        size: 'sm',
        initialValues,
        dialogClassName: dialogStyles.dialog,
      });
    },
    [data, dialogStyles.dialog, open, parentEntity]
  );

  const loadIndex = useCallback(
    (node: StructMapData, data: ImageViewerData<StructMapData>[]) =>
      data.findIndex(({ metadata }) => node.id === metadata.id),
    []
  );

  const onLeafClick = useCallback<OnLeafClickFn>(
    ({ node, parent }) => {
      if (data.length < 2) {
        return;
      }
      // do nothing when its not an image
      if (!isImageFromContentType(node.fileRef?.contentType)) {
        return;
      }

      // The parent data is already in state
      if (parentId.current && parentId.current === parent?.id) {
        setDefaultIndex(loadIndex(node, data));
        return;
      }

      parentId.current = parent?.id;
      const imageViewerData = loadNodeImages(parent);
      const index = loadIndex(node, imageViewerData);

      setData(imageViewerData);
      setDefaultIndex(index);
    },
    [data, loadIndex, loadNodeImages]
  );

  const toolbarButtons = useMemo<
    ImageViewerProps<StructMapData>['toolbarButtons']
  >(
    () => [
      ({ index }) => (
        <Tooltip title="rozšířit na celou obrazovku" placement="bottom">
          <Button onClick={openInFullscreenFactory(index)}>
            <FullscreenIcon />
          </Button>
        </Tooltip>
      ),
    ],
    [openInFullscreenFactory]
  );

  return useMemo(
    () => ({
      imageViewerData: data,
      onLeafClick,
      defaultIndex,
      toolbarButtons,
    }),
    [data, onLeafClick, defaultIndex, toolbarButtons]
  );
};
